'use client';

import { CommerceQueryProvider } from '@contexts/CommerceQueryProvider';
import { useCurrency } from '@contexts/CurrencyContext';
import { useBridgeTokenProvisioning } from '@lib/commerce/useBridgeToken';
import { ACTIVE_ORGANIZATION_ID_COOKIE } from '@lib/constants/cookies';
import { createCommerceAxiosClient } from '@lib/http/commerceAxiosClient';
import { failingFetcher } from '@lib/http/fetch';
import { Organization } from '@lib/types/api/Organization';
import { resolveActiveOrg } from '@lib/util/resolveActiveOrg';
import {
  CartManagementProvider,
  type CartManagementValueContext,
} from '@unity/commerce-core';
import Cookies from 'js-cookie';
import { useSession } from 'next-auth/react';
import { useParams } from 'next/navigation';
import { PropsWithChildren, useMemo } from 'react';
import useSWR from 'swr';

const STORE = 'asset';

type Environment = CartManagementValueContext['env'];

// Maps our NEXT_PUBLIC_ENVIRONMENT value to a valid commerce-core environment.
const ENVIRONMENT_BY_NEXT_ENV: Readonly<Record<string, Environment>> = {
  development: 'development',
  test: 'test',
  ephemeral: 'test',
  staging: 'staging',
  production: 'production',
};

const resolveEnv = (): Environment => {
  // Static reference required: Next.js only inlines NEXT_PUBLIC_* into the
  // client bundle for literal `process.env.NEXT_PUBLIC_*` access. A dynamic
  // lookup (getEnvVar) stays undefined in the browser, so this client
  // component would always hit the fallback below regardless of environment.
  const value = process.env.NEXT_PUBLIC_ENVIRONMENT;
  return ENVIRONMENT_BY_NEXT_ENV[value ?? ''] ?? 'staging';
};

type CommerceCartProviderProps = PropsWithChildren;

export const CommerceCartProvider = ({
  children,
}: CommerceCartProviderProps) => {
  const { currency } = useCurrency();
  const { data: session } = useSession();
  const params = useParams<{ locale: string }>();
  const { isReady: isBridgeTokenReady } = useBridgeTokenProvisioning();
  // CartManagementProvider seeds initialValues into a reducer once, so the
  // client instance can never be swapped after mount (CPS-7658).
  const client = useMemo(() => createCommerceAxiosClient('usg-direct'), []);
  const genesisUser = session?.accessToken?.genesis_user;
  const userId = typeof genesisUser === 'string' ? genesisUser : undefined;

  // OrganizationsProvider only wraps the header's UserMenu, so re-fetch here.
  const { data: orgsData } = useSWR<{
    organizations: Organization[];
    defaultOrganizationId?: string;
  }>(session?.accessToken ? '/api/users/organizations' : null, failingFetcher);

  // Org switching triggers a hard navigation, so the cookie is stable per mount.
  const activeOrganization = useMemo(
    () =>
      resolveActiveOrg(
        Cookies.get(ACTIVE_ORGANIZATION_ID_COOKIE),
        orgsData?.organizations,
        orgsData?.defaultOrganizationId
      ),
    [orgsData]
  );

  const initialValues: Omit<
    CartManagementValueContext,
    'locale' | 'organizationId' | 'userId'
  > = {
    store: STORE,
    client,
    currencyCode: currency,
    // commerce-core gates cart fetch on a non-empty countryCode; derive
    // from locale (e.g. 'en-US' → 'US') so cart-bound actions resolve.
    countryCode: params?.locale?.split('-')[1]?.toUpperCase() || 'US',
    env: resolveEnv(),
    // Start false so no cart is created on PDP load; the hook flips this on
    // only when the user clicks Buy Now.
    enableCartFetching: false,
    ensureCreateAuthenticatedCart: true,
    enableAnonymousCart: false,
    // Re-price the cart in the active currency on fetch, so a footer currency
    // switch is reflected before checkout.
    syncCartCurrency: true,
  };

  return (
    <CommerceQueryProvider>
      <CartManagementProvider
        locale={params?.locale}
        userId={userId}
        // Withheld until the bridge token exists: an org id is all
        // OrganizationProvider needs to fire an org bridge call, and a failed
        // mint must not turn that into an unauthenticated request.
        organizationId={isBridgeTokenReady ? activeOrganization?.id : undefined}
        initialValues={initialValues}
      >
        {children}
      </CartManagementProvider>
    </CommerceQueryProvider>
  );
};
